import { UAParser } from 'ua-parser-js'
import * as THREE from 'three'

const getRandomNumber = (min: number, max: number, use_decimals?: boolean) => {
  if (!use_decimals) {
    return Math.round(Math.random() * (max - min) + min)
  }

  return Math.random() * (max - min) + min
}

const getRandomColor = () => {
  return `hsla(${getRandomNumber(0, 360)}, ${getRandomNumber(45, 65)}%, ${getRandomNumber(40, 65)}%, 1.0)`
}

const isMobile = () => {
  // UAParser returns undefined for desktop: https://github.com/faisalman/ua-parser-js/issues/182
  const parserResults = UAParser()
  return !!parserResults.device.type
}

const isiOSMobile = () => {
  const parserResults = UAParser()
  return (parserResults.os.name === 'iOS' || parserResults.os.name === 'Mac OS') && isMobile()
}

const usingFirefox = () => {
  const recommendedEngines = ['blink', 'webkit']
  const parserResults = UAParser()

  if (!parserResults.engine.name) return true

  return !recommendedEngines.includes(parserResults.engine.name.toLowerCase())
}

const wait = (ms: number) => {
  return new Promise<void>((resolve) => {
    setTimeout(() => resolve(), ms)
  })
}

const determineYawToPointTo = (target_position: THREE.Vector3, object_position: THREE.Vector3) => {
  // Calculate direction vector from bodyB to bodyA
  const direction = new THREE.Vector3()
  direction.subVectors(target_position, object_position)

  // Calculate yaw angle - Note: z is the forward axis in Three.js
  // I have no idea how atan2 works
  const yaw = Math.atan2(direction.x, direction.z)

  // Convert radians to degrees
  let yawDegrees = THREE.MathUtils.radToDeg(yaw)
  if (yawDegrees < 0) yawDegrees += 360

  return yawDegrees
}

const shouldSpinYawClockwise = (current_yaw: number, target_yaw: number) => {
  const directions = [
    {
      name: 'forward_right',
      condition: (yaw: number) => yaw >= 112.5 && yaw < 157.5,
      clockwise_directions: ['backward_left', 'leftward', 'forward_left', 'forward'],
    },
    {
      name: 'forward_left',
      condition: (yaw: number) => yaw >= 202.5 && yaw < 247.5,
      clockwise_directions: ['backward', 'backward_left', 'leftward'],
    },
    {
      name: 'backward_right',
      condition: (yaw: number) => yaw >= 22.5 && yaw < 67.5,
      clockwise_directions: ['forward_left', 'forward', 'forward_right', 'rightward'],
    },
    {
      name: 'backward_left',
      condition: (yaw: number) => yaw >= 292.5 && yaw < 337.5,
      clockwise_directions: ['backward', 'backward_right', 'rightward', 'forward_right'],
    },
    {
      name: 'forward',
      condition: (yaw: number) => yaw >= 157.5 && yaw < 202.5,
      clockwise_directions: ['backward', 'backward_left', 'leftward', 'forward_left'],
    },
    {
      name: 'backward',
      condition: (yaw: number) => yaw >= 337.5 || yaw < 22.5,
      clockwise_directions: ['forward', 'forward_right', 'rightward', 'backward_right'],
    },
    {
      name: 'leftward',
      condition: (yaw: number) => yaw >= 247.5 && yaw < 292.5,
      clockwise_directions: ['rightward', 'backward_right', 'backward', 'backward_left'],
    },
    {
      name: 'rightward',
      condition: (yaw: number) => yaw >= 67.5 && yaw < 112.5,
      clockwise_directions: ['leftward', 'forward_left', 'forward', 'forward_right'],
    },
  ]

  const current_yaw_direction = directions.find((dir) => dir.condition(current_yaw))
  const target_yaw_direction = directions.find((dir) => dir.condition(target_yaw))

  if (!current_yaw_direction || !target_yaw_direction) {
    console.error('Error finding current or target yaw')
    console.log(current_yaw, target_yaw)
    return false
  }

  if (current_yaw_direction.name === target_yaw_direction.name) {
    const diff = current_yaw - target_yaw
    return diff > 0 ? diff < 180 : diff < -180
  }

  return target_yaw_direction.clockwise_directions.includes(current_yaw_direction.name)
}

const getYawInDegrees = (quaternion: THREE.Quaternion) => {
  // The rotation order matters a lot here. Use a different combination and you'll get inconsistent Y results.
  // When dealing with character or camera orientation, the yaw-pitch-roll convention is commonly used: Using 'YXZ'
  // aligns with the intuitive understanding of first rotating the character around the Y-axis to face a direction (yaw),
  // then adjusting the look up or down (pitch), and lastly applying any roll.
  // MUST use quaternion, do not pass the euler direction or you'll get wrong results and spin forever.
  const euler = new THREE.Euler().setFromQuaternion(quaternion, 'YXZ')
  let yaw = THREE.MathUtils.radToDeg(euler.y)

  // Convert yaw to the desired range (otherwise it'd be -180 to 180)
  if (yaw < 0) {
    yaw += 360
  }

  return yaw
}

const objectHasPropertiesValues = (
  objToCheck: { [key: string]: boolean },
  objToCheckAgainst: { [key: string]: boolean }
) => {
  for (const key of Object.keys(objToCheck)) {
    if (objToCheckAgainst[key] !== undefined && objToCheckAgainst[key] !== objToCheck[key]) {
      return false
    }
  }

  return true
}

const removeAllHelpersFromScene = (scene: THREE.Scene) => {
  scene.children
    .filter((child) => child instanceof THREE.Box3Helper)
    .forEach((helper) => {
      scene.remove(helper)
      if (helper.dispose) helper.dispose()
    })
}

function getBoxIntersection(boxA: THREE.Box3, boxB: THREE.Box3) {
  const intersectionMin = new THREE.Vector3(
    Math.max(boxA.min.x, boxB.min.x),
    Math.max(boxA.min.y, boxB.min.y),
    Math.max(boxA.min.z, boxB.min.z)
  )

  const intersectionMax = new THREE.Vector3(
    Math.min(boxA.max.x, boxB.max.x),
    Math.min(boxA.max.y, boxB.max.y),
    Math.min(boxA.max.z, boxB.max.z)
  )

  // If no overlap, return null
  if (
    intersectionMin.x > intersectionMax.x ||
    intersectionMin.y > intersectionMax.y ||
    intersectionMin.z > intersectionMax.z
  ) {
    return null
  }

  return new THREE.Box3(intersectionMin, intersectionMax)
}

function resolvePenetration(movableBox: THREE.Box3, obstacleBox: THREE.Box3, player: THREE.Object3D) {
  const intersection = getBoxIntersection(movableBox, obstacleBox)
  if (!intersection) return null

  const iSize = new THREE.Vector3()
  intersection.getSize(iSize)

  // Get the axis of smallest iSize
  let axis: 'x' | 'y' | 'z' = 'x'
  if (iSize.y < iSize.x && iSize.y < iSize.z) axis = 'y'
  else if (iSize.z < iSize.x && iSize.z < iSize.y) axis = 'z'

  // Direction from obstacle to movable
  const movableCenter = movableBox.getCenter(new THREE.Vector3())
  const obstacleCenter = obstacleBox.getCenter(new THREE.Vector3())
  const direction = movableCenter.sub(obstacleCenter)

  const push = new THREE.Vector3()
  push[axis] = direction[axis] > 0 ? iSize[axis] : -iSize[axis]

  player.position.add(push)

  return { axis, offset_applied: push[axis] }
}

export {
  getRandomNumber,
  getRandomColor,
  isMobile,
  wait,
  determineYawToPointTo,
  shouldSpinYawClockwise,
  objectHasPropertiesValues,
  isiOSMobile,
  usingFirefox,
  removeAllHelpersFromScene,
  getBoxIntersection,
  resolvePenetration,
  getYawInDegrees,
}
